import React from 'react';

import { classnames, useClassnames } from '@lumapps/classnames';
import { mdiFileDocument, mdiReplay, mdiUploadOutline, mdiTrashCanOutline, mdiOpenInNew } from '@lumapps/lumx/icons';
import {
    Emphasis,
    FlexBox,
    FlexBoxProps,
    Icon,
    IconButton,
    Orientation,
    Uploader,
    UploaderProps,
    Size,
    Text,
    IconButtonProps,
    ButtonProps,
    Button,
    Alignment,
    Typography,
    ColorVariant,
} from '@lumapps/lumx/react';
import { GLOBAL, useTranslate } from '@lumapps/translations';

import './index.scss';

export interface UploadFileControlledProps extends UploaderProps {
    /** component that will be displayed before the uploader */
    before?: React.ReactNode;
    /** component that will be displayed after the uploader */
    after?: React.ReactNode;
    /** custom class name for Uploader */
    className?: string;
    /** input wrapper props */
    inputWrapperProps?: FlexBoxProps;
    /** callback called clicking on picker */
    onOpenPicker?: () => void;
    /** callback called when the user click on the delete button */
    onDelete?: (id?: string) => void;
    /** callback called when the user click on the edit button */
    onEdit?: () => void;
    /** should display edit button */
    canEdit?: boolean;
    /** props that will override the one in the edit button */
    editButtonOverrideProps?: Partial<IconButtonProps>;
    /** props that will override the one in the delete button */
    deleteButtonOverrideProps?: Partial<IconButtonProps>;
    /** Selected file */
    selectedFile?: File & { id?: string };
    /** Selected files */
    selectedFiles?: (File & { id?: string })[];
    /** Mode of the upload component: uploader | button */
    mode?: 'uploader' | 'button';
    /** Button props */
    buttonProps?: Partial<ButtonProps>;
    /** Can handle multiple files (and can add additional files after some are uploaded) */
    multiFile?: boolean;
    /** Is the component full width */
    fullWidth?: boolean;
    /** Uploader helper */
    helper?: React.ReactNode;
    /** the link file */
    linkFile?: string;
}

const CLASSNAME = 'lumx-upload-file';

/**
 * Pure UI component that lets you upload a file with a file picker.
 * lets you manage you selectedFile
 * The file and its proprieties can be accessed with the onSelection prop.
 * This component doesn't manage the logic of the upload.
 *
 * @family Files
 */
export const UploadFileControlled: React.FC<UploadFileControlledProps> = ({
    before,
    after,
    className,
    inputWrapperProps,
    textProps,
    onOpenPicker,
    onDelete,
    onEdit,
    canEdit = true,
    editButtonOverrideProps,
    deleteButtonOverrideProps,
    selectedFiles,
    selectedFile,
    mode = 'uploader',
    buttonProps,
    multiFile,
    fullWidth,
    linkFile,
    icon,
    helper,
    ...props
}) => {
    const { translateKey } = useTranslate();

    const { element } = useClassnames(CLASSNAME);

    const hasFiles = selectedFile || (selectedFiles && selectedFiles.length > 0);

    const displayUploader = () => {
        if (!multiFile && hasFiles) {
            return null;
        }
        if (mode === 'button' || hasFiles) {
            return (
                <Button
                    id={props.id}
                    onClick={onOpenPicker}
                    leftIcon={icon || mdiUploadOutline}
                    emphasis={Emphasis.low}
                    fullWidth={fullWidth}
                    className={element('button')}
                    {...buttonProps}
                >
                    {props.label}
                </Button>
            );
        }

        return (
            <Uploader
                {...props}
                icon={icon}
                onClick={onOpenPicker}
                className={element('uploader', { 'full-width': Boolean(fullWidth) })}
            />
        );
    };

    const displaySelectedFile = (file?: File & { id?: string }) => {
        if (!file) {
            return null;
        }
        return (
            <FlexBox orientation={Orientation.horizontal} gap="regular" hAlign="center" key={file.id}>
                <FlexBox
                    {...inputWrapperProps}
                    orientation={Orientation.horizontal}
                    className={element('input-wrapper', [inputWrapperProps?.className])}
                    vAlign={Alignment.spaceBetween}
                    fillSpace={fullWidth}
                >
                    <FlexBox orientation={Orientation.horizontal} fillSpace className={element('file-block')}>
                        <Icon
                            size={Size.s}
                            className={element('document-icon')}
                            onClick={onEdit}
                            label={translateKey(GLOBAL.EDIT)}
                            icon={mdiFileDocument}
                            hasShape
                        />
                        <FlexBox className={element('text-wrapper')} orientation={Orientation.horizontal} fillSpace>
                            <Text as="strong" className={element('text', [textProps?.className])} {...textProps}>
                                {file.name}
                            </Text>
                        </FlexBox>
                    </FlexBox>
                    <FlexBox orientation={Orientation.horizontal} className={element('icon-block')}>
                        <div className={element('edit')}>
                            {canEdit && (
                                <IconButton
                                    size={Size.s}
                                    className={element('edit-btn')}
                                    onClick={onEdit}
                                    label={translateKey(GLOBAL.EDIT)}
                                    emphasis={Emphasis.low}
                                    icon={mdiReplay}
                                    {...editButtonOverrideProps}
                                />
                            )}
                        </div>
                        {linkFile && (
                            <IconButton
                                href={linkFile}
                                target="_blank"
                                label={translateKey(GLOBAL.OPEN_NEW_TAB)}
                                size={Size.s}
                                emphasis={Emphasis.low}
                                icon={mdiOpenInNew}
                            />
                        )}
                        {onDelete && (
                            <div className={element('delete')}>
                                <IconButton
                                    size={Size.s}
                                    className={element('delete-btn')}
                                    onClick={() => onDelete(file.id)}
                                    label={translateKey(GLOBAL.DELETE)}
                                    emphasis={Emphasis.low}
                                    icon={mdiTrashCanOutline}
                                    {...deleteButtonOverrideProps}
                                />
                            </div>
                        )}
                    </FlexBox>
                </FlexBox>
            </FlexBox>
        );
    };
    return (
        <FlexBox
            orientation={Orientation.vertical}
            className={classnames(CLASSNAME, className, fullWidth && 'full-width')}
            gap={Size.regular}
        >
            {before}

            {displayUploader()}
            {helper && (
                <Text typography={Typography.caption} color="dark" colorVariant={ColorVariant.L2} as="p">
                    {helper}
                </Text>
            )}
            {hasFiles && (
                <FlexBox orientation={Orientation.vertical} gap={Size.regular}>
                    {!multiFile && displaySelectedFile(selectedFile || selectedFiles?.[0])}
                    {multiFile &&
                        selectedFiles &&
                        selectedFiles.length > 0 &&
                        selectedFiles.map((file) => displaySelectedFile(file))}
                </FlexBox>
            )}
            {after}
        </FlexBox>
    );
};
